import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, Users, Eye, ArrowUpRight, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function AnalyticsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/admin/analytics?days=${range}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [range, token]);

  if (loading) {
    return (
      <div className="text-center py-12 text-stone-500" data-testid="analytics-loading">Loading analytics…</div>
    );
  }

  if (!data) {
    return (
      <Card className="bg-white">
        <CardContent className="py-12 text-center text-stone-500" data-testid="analytics-empty">
          No analytics available.
        </CardContent>
      </Card>
    );
  }

  const { totals, daily, top_pages, top_referrers } = data;
  const maxViews = Math.max(1, ...daily.map(d => d.views));

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Range selector */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-600">
          Showing last <strong>{range}</strong> days of site traffic
        </p>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              size="sm"
              variant={range === d ? 'default' : 'outline'}
              onClick={() => setRange(d)}
              className={range === d ? 'bg-primary text-white' : ''}
              data-testid={`analytics-range-${d}`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Page Views" value={totals.page_views.toLocaleString()} icon={Eye} color="text-blue-600" bg="bg-blue-50" testId="kpi-views" />
        <KpiCard label="Unique Visitors" value={totals.unique_visitors.toLocaleString()} icon={Users} color="text-primary" bg="bg-primary-50" testId="kpi-visitors" />
        <KpiCard label="New Signups" value={totals.signups.toLocaleString()} icon={ArrowUpRight} color="text-green-600" bg="bg-green-50" testId="kpi-signups" />
        <KpiCard label="Signup Rate" value={`${totals.conversion_rate}%`} icon={TrendingUp} color="text-amber-600" bg="bg-amber-50" testId="kpi-conv" />
      </div>

      {/* Daily chart */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Daily Traffic</CardTitle>
        </CardHeader>
        <CardContent>
          {daily.length === 0 ? (
            <p className="text-sm text-stone-500 text-center py-8">No traffic yet in this window. Once visitors hit the site, they'll appear here.</p>
          ) : (
            <div className="flex items-end gap-1 h-40" data-testid="analytics-chart">
              {daily.map(d => {
                const heightPct = Math.max(4, (d.views / maxViews) * 100);
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                    <div
                      className="w-full bg-primary/70 hover:bg-primary rounded-t transition-colors cursor-pointer"
                      style={{ height: `${heightPct}%` }}
                    />
                    <div className="opacity-0 group-hover:opacity-100 absolute -top-12 bg-stone-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none z-10">
                      {d.date}: {d.views} views, {d.visitors} visitors
                    </div>
                    <span className="text-[9px] text-stone-400 rotate-45 origin-top-left mt-1 whitespace-nowrap">
                      {d.date.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top pages & referrers */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Top Pages</CardTitle>
          </CardHeader>
          <CardContent>
            {top_pages.length === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center">No page views yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="top-pages-list">
                {top_pages.map(p => (
                  <li key={p.path} className="flex items-center justify-between text-sm py-2 border-b border-stone-100 last:border-0">
                    <span className="text-stone-800 truncate max-w-xs font-mono text-xs">{p.path}</span>
                    <span className="text-stone-500 font-medium">{p.views}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_referrers.length === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center">No referrers yet — most visitors are typing your URL directly or clicking bookmarks.</p>
            ) : (
              <ul className="space-y-2" data-testid="top-referrers-list">
                {top_referrers.map(r => (
                  <li key={r.source} className="flex items-center justify-between text-sm py-2 border-b border-stone-100 last:border-0">
                    <span className="text-stone-800 truncate max-w-xs">{r.source}</span>
                    <span className="text-stone-500 font-medium">{r.views}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, bg, testId }) {
  return (
    <Card className="bg-white" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          <div>
            <p className="text-xl font-bold text-stone-900">{value}</p>
            <p className="text-xs text-stone-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
